import { useState, useEffect, useMemo } from "react";
import DataTable from "../../../../../Common/DataTable/DataTable";
import DataTableFooter from "../../../../../Common/DataTableFooter/DataTableFooter";
import DataTableHeader from "../../../../../Common/DataTableHeader/DataTableHeader";
import { useDispatch, useSelector } from "react-redux";
import {
  GET_ALL_MAIN_REPORTS_REQUEST,
  GET_ALL_DEPARTMENT_LIST_REQUEST,
} from "../../../../../Utils/Constant";
import * as XLSX from "xlsx";
import RenderChart from "./RenderChart";
import DateSelector from "../../../../Universal/Dashboard/Components/DateSelector";
import { toast } from "react-toastify";
import { getBaseUrl } from "../../../../../Redux/Config/AppConfig";

const ReportsMainTable = ({ setSelectedCategory }) => {
  const [selectedReportType, setSelectedReportType] = useState("");
  const [selectedDepartmentType, setSelectedDepartmentType] = useState("");
  const [reportData, setReportData] = useState([]);
  const [reportColumns, setReportColumns] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [globalFilterType, setGlobalFilterType] = useState("name");

  const [reportOptions, setReportOptions] = useState([]);
  const dispatch = useDispatch();
  const { mainReports: main1, loading } = useSelector(
    (state) => state.dashBoard
  );
  const [mainReports, setMainReport] = useState([]);
  const { departmentList } = useSelector((state) => state.department);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [granularity, setGranularity] = useState("day");

  // Helper functions for date range calculation
  function getISOWeekNumber(date) {
    const tmp = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    return Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  }
  function getStartAndEndOfISOWeek(isoWeekString) {
    if (!isoWeekString || !isoWeekString.includes("-W")) {
      const now = new Date();
      const year = now.getUTCFullYear();
      const week = getISOWeekNumber(now);
      isoWeekString = `${year}-W${String(week).padStart(2, "0")}`;
    }
    const [yearStr, weekStr] = isoWeekString.split("-W");
    const year = Number(yearStr);
    const week = Number(weekStr);
    if (!year || !week) {
      const now = new Date();
      const year = now.getUTCFullYear();
      const week = getISOWeekNumber(now);
      return getStartAndEndOfISOWeek(
        `${year}-W${String(week).padStart(2, "0")}`
      );
    }
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4DayOfWeek = jan4.getUTCDay() || 7;
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setUTCDate(jan4.getUTCDate() - (jan4DayOfWeek - 1));
    const monday = new Date(mondayOfWeek1);
    monday.setUTCDate(mondayOfWeek1.getUTCDate() + (week - 1) * 7);
    monday.setUTCHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    sunday.setUTCHours(23, 59, 59, 999);
    return {
      startDate: monday.toISOString(),
      endDate: sunday.toISOString(),
    };
  }
  function getStartAndEndOfMonth(yyyyMM) {
    const [year, month] = yyyyMM.split("-").map(Number);
    const startDate = new Date(year, month - 1, 1, 0, 0, 0, 0);
    const endDate = new Date(year, month, 0, 23, 59, 59, 999);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }
  function getStartAndEndOfYear(yyyy) {
    const year = Number(yyyy);
    const startDate = new Date(year, 0, 1, 0, 0, 0, 0);
    const endDate = new Date(year, 11, 31, 23, 59, 59, 999);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }

  // Format payment method for display
  const formatPaymentMethod = (paymentMethod) => {
    if (!paymentMethod) return "";

    const methodMap = {
      cash: "Cash",
      debit_card: "Debit Card",
      upi: "UPI",
      credit_card: "Credit Card",
      net_banking: "Net Banking",
      bank_transfer: "Bank Transfer",
    };

    return methodMap[paymentMethod.toLowerCase()] || paymentMethod;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format date and time for upcoming bookings
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const timePart = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${datePart} ${timePart}`;
    } catch (error) {
      return dateString;
    }
  };

  // Format boolean values for display
  const formatBoolean = (value) => {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return value;
  };

  // API call effect
  useEffect(() => {
    dispatch({
      type: GET_ALL_DEPARTMENT_LIST_REQUEST,
    });
  }, []);

  // For main reports
  useEffect(() => {
    if (!selectedReportType) return;
    if (
      granularity === "custom" &&
      (!selectedDate?.from || !selectedDate?.to)
    ) {
      return;
    }
    fetchReportsData();
  }, [page, pageSize, selectedReportType, selectedDate]);

  // Update fetchReportsData to always send filterType, startDate, endDate for all report types
  const fetchReportsData = async () => {
    let startDate = selectedDate;
    let endDate = selectedDate;
    let filterType = granularity;
    if (granularity === "week" && selectedDate && selectedDate.includes("-W")) {
      const { startDate: s, endDate: e } =
        getStartAndEndOfISOWeek(selectedDate);
      startDate = s;
      endDate = e;
    } else if (granularity === "month") {
      const { startDate: s, endDate: e } = getStartAndEndOfMonth(selectedDate);
      startDate = s;
      endDate = e;
    } else if (granularity === "year") {
      const { startDate: s, endDate: e } = getStartAndEndOfYear(
        selectedDate.slice(0, 4)
      );
      startDate = s;
      endDate = e;
    } else if (
      granularity === "custom" &&
      selectedDate.from &&
      selectedDate.to
    ) {
      startDate = selectedDate.from + "T00:00:00";
      endDate = selectedDate.to + "T23:59:59";
    } else {
      startDate = selectedDate + "T00:00:00";
      endDate = selectedDate + "T23:59:59";
    }
    // Ensure startDate and endDate have correct time boundaries
    if (startDate && !startDate.includes("T")) {
      startDate = startDate + "T00:00:00";
    }
    if (endDate && !endDate.includes("T")) {
      endDate = endDate + "T23:59:59";
    }
    const hotel_id = localStorage.getItem("hotel_id");
    const params = new URLSearchParams({
      page: page,
      limit: pageSize,
      path: selectedReportType,
    });
    if (sortBy) params.set("sortBy", sortBy);
    if (sortOrder) params.set("sortOrder", sortOrder);
    // if (name) params.set("name", name);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    if (filterType) params.set("filterType", filterType);
    if (hotel_id) params.set("hotel_id", hotel_id);

    try {
      let baseUrl = getBaseUrl();
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${baseUrl}/report/${selectedReportType}?${params}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        let result = await response.json();
        setMainReport(result);
      } else {
        setMainReport([]);
      }
    } catch (error) {
      toast.error("Failed to fetch offers.");
    }
    // dispatch({
    //   type: GET_ALL_MAIN_REPORTS_REQUEST,
    //   payload: {
    //     page,
    //     limit: pageSize,
    //     path: selectedReportType,
    //     startDate,
    //     endDate,
    //     filterType,
    //   },
    // });
  };

  // Update data and columns when mainReports changes
  useEffect(() => {
    if (mainReports && mainReports?.data) {
      let data =
        (selectedReportType === "inventory-consumption"
          ? mainReports?.data?.itemWiseConsumption
          : selectedReportType === "pos-reports"
          ? mainReports?.data?.item_sales_summary
          : selectedReportType === "operational-pl" ||
            selectedReportType === "expense-analysis" ||
            selectedReportType === "agent-corporate-list" ||
            selectedReportType === "budget-variance-alert"
          ? [mainReports?.data] // Assume backend returns array of history/forecast objects
          : mainReports?.data) || [];

      let normalizedData = (data?.length ? data : [])?.map((item) => {
        if (selectedReportType === "operational-pl") {
          return {
            room_revenue: item.revenue?.hotel_revenue?.total_hotel_revenue ?? 0,
            restaurant_revenue: item.revenue?.restaurant_revenue ?? 0,
            total_revenue: item.revenue?.total_revenue ?? 0,
            room_expense: item.expenses?.by_type?.Room ?? 0,
            total_expense: item.expenses?.total ?? 0,
            gross_profit: item.profit?.gross_profit ?? 0,
            profit_margin: `${item.profit?.profit_margin ?? 0}%`,
            period_start: formatDate(item.period?.start_date),
            period_end: formatDate(item.period?.end_date),
          };
        }
        if (selectedReportType === "history-forecast") {
          // Direct mapping for history-forecast
          return {
            date: item.date,
            actual_bookings: item.actual_bookings,
            actual_revenue: item.actual_revenue,
            forecast_bookings: item.forecast_bookings,
            forecast_revenue: item.forecast_revenue,
          };
        }
        if (selectedReportType === "expense-analysis") {
          // Use expenses_by_category and expenses_by_date for table/chart
          let rows = [];
          if (item.expenses_by_category) {
            rows = rows.concat(
              Object.entries(item.expenses_by_category).map(
                ([category, amount]) => ({
                  category,
                  amount,
                })
              )
            );
          }
          // Optionally, you could also add date rows:
          // if (item.expenses_by_date) {
          //   rows = rows.concat(
          //     Object.entries(item.expenses_by_date).map(([date, amount]) => ({
          //       date,
          //       amount,
          //     }))
          //   );
          // }
          return rows;
        }
        if (selectedReportType === "payment-types") {
          return item;
        }
        if (selectedReportType === "upcoming-bookings") {
          return {
            ...item,
            check_in_date_time: formatDateTime(item.check_in_date_time),
          };
        }
        if (selectedReportType === "pos-reports") {
          // Normalize item_sales_summary for table/chart
          return {
            item_name: item.item_name,
            total_quantity: Number(item.total_quantity),
            total_value: Number(item.total_value),
            subcategory_name:
              item["subcategory.name"] || item.subcategory_name || "",
          };
        }
        if (selectedReportType === "agent-corporate-list") {
          // Flatten all booking types into a single array with booking_type
          let rows = [];
          const types = ["corporate", "group", "individual"];
          types.forEach((type) => {
            if (item[type] && Array.isArray(item[type].reservations)) {
              rows = rows.concat(
                item[type].reservations.map((res) => ({
                  booking_type: type.charAt(0).toUpperCase() + type.slice(1),
                  ...res,
                  check_in_date_time: formatDateTime(res.check_in_date_time),
                  check_out_date_time: formatDateTime(res.check_out_date_time),
                }))
              );
            }
          });
          return rows;
        }
        if (selectedReportType === "inventory-cost-change-report") {
          // Normalize inventory cost change data for table/chart
          return {
            item_name: item.item?.name || "",
            unit: item.item?.unit || "",
            old_cost: Number(item.old_cost),
            new_cost: Number(item.new_cost),
            changed_by: item.changed_by,
            changed_at: formatDate(item.changed_at),
            reason: item.reason,
          };
        }
        if (selectedReportType === "budget-variance-alert") {
          // Normalize budget variance alert data for table/chart
          return {
            budget_period_start: item.budget_period?.start_date,
            budget_period_end: item.budget_period?.end_date,
            total_revenue: Number(item.revenue_analysis?.total_revenue || 0),
            hotel_revenue: Number(item.revenue_analysis?.hotel_revenue || 0),
            restaurant_revenue: Number(
              item.revenue_analysis?.restaurant_revenue || 0
            ),
            budgeted_expenses: Number(
              item.expense_analysis?.budgeted_expenses || 0
            ),
            actual_expenses: Number(
              item.expense_analysis?.actual_expenses || 0
            ),
            expense_variance: Number(
              item.expense_analysis?.expense_variance || 0
            ),
            expense_variance_percent: Number(
              item.expense_analysis?.expense_variance_percent || 0
            ),
            procurement_expenses: Number(
              item.expense_analysis?.expense_breakdown?.procurement_expenses ||
                0
            ),
            custom_expenses: Number(
              item.expense_analysis?.expense_breakdown?.custom_expenses || 0
            ),
            actual_profit: Number(item.profit_analysis?.actual_profit || 0),
            profit_margin: Number(item.profit_analysis?.profit_margin || 0),
            expense_alert: formatBoolean(item.alerts?.expense_alert || false),
            profit_alert: formatBoolean(item.alerts?.profit_alert || false),
            alert: formatBoolean(item.alerts?.overall_alert || false),
            alert_messages: item.alerts?.alert_messages || [],
            performance_status: item.performance_summary?.status || "",
            key_issues: item.performance_summary?.key_issues || "",
          };
        }
        return {
          ...item,
          ...(item?.guest?.first_name && { guest_name: item.guest.first_name }),
          ...(item.hasOwnProperty("booking_type") && {
            booking_type: item.booking_type || "N/A",
          }),
          ...(item?.check_in_date_time && {
            check_in_date_time: item.check_in_date_time.split(":00")[0],
          }),
          ...(item?.item?.name && {
            item_name: item?.item?.name,
          }),
          ...(item?.item?.quantity >= 0 && {
            current_stock: item?.item?.quantity,
          }),
          ...(item?.item?.unit && {
            unit: item?.item?.unit,
          }),
          ...(item?.totalDecrease >= 0 && {
            total_consumption: item?.totalDecrease,
          }),
          ...(item?.totalProcurement >= 0 && {
            total_procurement: item?.totalProcurement,
          }),
          ...(item?.item?.department?.name && {
            department: item?.item?.department?.name,
          }),
          ...(item?.item?.category?.name && {
            category: item?.item?.category?.name,
          }),
        };
      });

      if (selectedReportType === "expense-analysis") {
        normalizedData = normalizedData.flat();
      }
      if (selectedReportType === "agent-corporate-list") {
        normalizedData = normalizedData.flat();
      }
      if (selectedReportType === "inventory-cost-change-report") {
        normalizedData = normalizedData;
      }

      setReportData(normalizedData?.length ? normalizedData : []);

      if (mainReports?.columns) {
        setReportColumns(mainReports.columns);
      } else {
        if (normalizedData?.length > 0) {
          const firstItem = normalizedData[0];

          let allowedColumns = [
            "item_name",
            "current_quantity",
            "unit",
            "department",
            "category",
            "status",
            "booking_id",
            "booking_type",
            "booking_status",
            "total_rooms",
            "check_in_date_time",
            "guest_name",
            "total_amount",
            "current_stock",
            "total_consumption",
            "total_procurement",
          ];
          if (selectedReportType === "history-forecast") {
            allowedColumns = [
              "date",
              "actual_bookings",
              "actual_revenue",
              "forecast_bookings",
              "forecast_revenue",
            ];
          }
          if (selectedReportType === "operational-pl") {
            allowedColumns = [
              "room_revenue",
              "restaurant_revenue",
              "total_revenue",
              "room_expense",
              "total_expense",
              "gross_profit",
              "profit_margin",
              "period_start",
              "period_end",
            ];
          }
          if (selectedReportType === "expense-analysis") {
            allowedColumns = ["category", "amount"];
          }
          if (selectedReportType === "pos-reports") {
            allowedColumns = [
              "item_name",
              "total_quantity",
              "total_value",
              "subcategory_name",
            ];
          }
          if (selectedReportType === "agent-corporate-list") {
            allowedColumns = [
              "booking_type",
              // "reservation_id",
              "guest_name",
              "email",
              "phone",
              "booking_id",
              "check_in_date_time",
              "check_out_date_time",
              "total_amount",
            ];
          }
          if (selectedReportType === "inventory-cost-change-report") {
            allowedColumns = [
              "item_name",
              "unit",
              "old_cost",
              "new_cost",
              // "changed_by",
              "changed_at",
              // "reason",
            ];
          }
          if (selectedReportType === "budget-variance-alert") {
            allowedColumns = [
              "budget_period_start",
              "budget_period_end",
              "total_revenue",
              "budgeted_expenses",
              "actual_expenses",
              "expense_variance",
              "actual_profit",
              // "expense_alert",
              // "profit_alert",
              "alert",
              // "performance_status",
              // "key_issues",
            ];
          }

          const generatedColumns = Object.keys(firstItem)
            .filter((key) => allowedColumns?.includes(key))
            .map((key) => ({
              Header: key
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              accessor: key,
            }));

          setReportColumns(generatedColumns);
        }
      }
    }
  }, [mainReports]);

  useEffect(() => {
    if (selectedDepartmentType) {
      let data = departmentList?.data?.departments?.find(
        (item) => item.id === selectedDepartmentType
      );
      if (data?.name?.toLowerCase() === "front desk") {
        setReportOptions([
          { label: "Upcoming Booking", value: "upcoming-bookings" },
          { label: "History & Forecast", value: "history-forecast" },
          { label: "Booking Types Report", value: "agent-corporate-list" },
        ]);
      } else if (data?.name?.toLowerCase() === "inventory") {
        setReportOptions([
          { label: "Stock Level", value: "stock-level" },
          { label: "Inventory Consumption", value: "inventory-consumption" },
          {
            label: "Inventory Cost Change Report",
            value: "inventory-cost-change-report",
          },
        ]);
      } else if (data?.name?.toLowerCase() === "management") {
        setReportOptions([
          { label: "Operational Profit & Loss (P&L)", value: "operational-pl" },
          { label: "Expense Analysis", value: "expense-analysis" },
          { label: "Payment Methods Report", value: "payment-types" },
          {
            label: "POS Item Sales Report (Basic - quantities & value)",
            value: "pos-reports",
          },
          { label: "Budget Variance Alert", value: "budget-variance-alert" },
        ]);
      } else {
        setReportOptions([]);
      }
      setSelectedReportType("");
    }
  }, [selectedDepartmentType]);

  // Memoize category options
  const departmentOptions = useMemo(() => {
    const allowed = ["front desk", "inventory", "management"];
    return (
      departmentList?.data?.departments
        ?.filter((category) => allowed.includes(category?.name?.toLowerCase()))
        .map((category) => ({
          label: category.name,
          value: category.id,
        })) || []
    );
  }, [departmentList]);

  // Filter data based on global filter
  const filteredData = reportData?.filter((item) => {
    if (!globalFilter) return true;

    return Object.values(item).some((value) => {
      if (typeof value === "string") {
        return value?.toLowerCase()?.includes(globalFilter?.toLowerCase());
      }
      if (typeof value === "number") {
        return value.toString().includes(globalFilter);
      }
      // Skip filtering array/object values like `rooms`
      return false;
    });
  });

  // Paginate data
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredData?.slice(startIndex, endIndex);

  const handleSortChange = (sort) => {
    setSortBy(sort.id);
    setSortOrder(sort.desc ? "desc" : "asc");
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleDepartmentTypeChange = (e) => {
    setSelectedDepartmentType(e.target.value);
  };
  const handleReportTypeChange = (e) => {
    setSelectedReportType(e.target.value);
    setPage(1); // Reset to first page when changing report type
    setGlobalFilter(""); // Clear filter when changing report type
  };

  // Calculate pagination info
  const totalEntries = filteredData?.length;
  const totalPages = Math.ceil(totalEntries / pageSize);

  const exportToExcel = () => {
    // Prepare data for export - all filtered data (not just current page)
    const exportData = filteredData?.map((item) => {
      const exportItem = {};
      reportColumns?.forEach((column) => {
        exportItem[column.Header] = item[column.accessor] || "";
      });
      return exportItem;
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, `${selectedReportType} Report`);

    // Generate filename with current date and total records count
    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `${selectedReportType}_report_${exportData?.length}_records_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  return (
    <div>
      {selectedReportType !== "stock-level" && (
        <DateSelector
          value={selectedDate}
          granularity={granularity}
          onChange={(date, gran) => {
            setSelectedDate(date);
            setGranularity(gran);
          }}
        />
      )}
      {/* Main Reports UI */}
      <>
        <div className="row mb-4">
          <div className="col-md-4">
            <label className="form-label fw-bold">Select Department Type</label>
            <select
              className="form-select"
              value={selectedDepartmentType}
              onChange={handleDepartmentTypeChange}
            >
              <option key={""} value={""}>
                Select Department
              </option>
              {departmentOptions?.map((option) => (
                <option key={option?.id} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-bold">Select Report Type</label>
            <select
              className="form-select"
              value={selectedReportType}
              onChange={handleReportTypeChange}
              disabled={!selectedDepartmentType || reportOptions?.length === 0}
            >
              <option key={""} value={""}>
                Select Report
              </option>
              {reportOptions?.map((option) => (
                <option key={option?.id} value={option?.value}>
                  {option?.label}
                </option>
              ))}
              {reportOptions?.length === 0 && (
                <option key={""} value={""}>
                  No options available
                </option>
              )}
            </select>
          </div>
        </div>

        {/* Show Payment Methods Report only if Management and Payment Methods Report are selected */}
        {departmentList?.data?.departments?.find(
          (item) =>
            item.id === selectedDepartmentType &&
            item?.name?.toLowerCase() === "management"
        ) &&
          selectedReportType === "budget-variance-alert" && (
            <div>
              {loading && <div>Loading...</div>}
              {!loading && reportData && reportData.length > 0 && (
                <div className="mb-4">
                  {/* Alert Summary Section */}
                  <div className="card mb-3">
                    <div className="card-header">
                      <h5 className="mb-0">Performance Summary</h5>
                    </div>
                    <div className="card-body">
                      {reportData[0].alert_messages &&
                        reportData[0].alert_messages.length > 0 && (
                          <div className="mt-3">
                            <h6>Alert Messages:</h6>
                            {reportData[0].alert_messages.map(
                              (message, idx) => (
                                <div key={idx} className="alert alert-danger">
                                  {message}
                                </div>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        {departmentList?.data?.departments?.find(
          (item) =>
            item.id === selectedDepartmentType &&
            item?.name?.toLowerCase() === "management"
        ) &&
          selectedReportType === "payment-types" && (
            <div>
              <h4>Payment Methods Report</h4>
              {loading && <div>Loading...</div>}
              {!loading && reportData && (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Payment Method</th>
                      <th>Total Amount</th>
                      <th>Hotel Transaction Count</th>
                      <th>Restaurant Transaction Count</th>
                      <th>Total Transaction Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData?.map((item, idx) => (
                      <tr key={idx}>
                        <td>{formatPaymentMethod(item.payment_method)}</td>
                        <td>{item.total_amount}</td>
                        <td>{item.hotel_transaction_count}</td>
                        <td>{item.restaurant_transaction_count}</td>
                        <td>{item.transaction_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!loading && (!reportData || reportData?.length === 0) && (
                <div>No payment methods data available.</div>
              )}
            </div>
          )}

        {/* Otherwise, show the regular report UI */}
        {!(
          departmentList?.data?.departments?.find(
            (item) =>
              item.id === selectedDepartmentType &&
              item?.name?.toLowerCase() === "management"
          ) && selectedReportType === "payment-types"
        ) && (
          <>
            {/* Show message if no data */}
            {!loading && reportData?.length === 0 && selectedReportType && (
              <div className="alert alert-info">No report data available.</div>
            )}

            {reportData?.length > 0 && selectedReportType && (
              <>
                <div className="row d-flex justify-content-center">
                  <div className="col-md-8">
                    {selectedReportType === "expense-analysis" ? (
                      <RenderChart
                        reportData={reportData}
                        selectedReportType={selectedReportType}
                        expenseAnalysisRaw={mainReports.data}
                      />
                    ) : (
                      <RenderChart
                        reportData={reportData}
                        selectedReportType={selectedReportType}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
            {selectedReportType && (
              <div className="row mb-4">
                <div className="col-md-12 d-flex justify-content-end">
                  <div>
                    <label className="form-label d-block invisible">
                      Export
                    </label>
                    <button
                      className="btn btn-success"
                      onClick={exportToExcel}
                      disabled={filteredData?.length === 0}
                    >
                      <i className=""></i>
                      Export
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Render table only if we have data */}
            {reportData?.length > 0 && selectedReportType && (
              <>
                <DataTableHeader
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  setCurrentPage={setPage}
                  globalFilterType={globalFilterType}
                  setGlobalFilterType={setGlobalFilterType}
                  flage="mainReports"
                />

                <DataTable
                  columns={reportColumns}
                  data={paginatedData}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  onSortChange={handleSortChange}
                  isLoading={loading}
                />

                <DataTableFooter
                  currentPage={page}
                  totalPages={totalPages}
                  setCurrentPage={handlePageChange}
                  totalEntries={totalEntries}
                  pageSize={pageSize}
                />
              </>
            )}
          </>
        )}
      </>
    </div>
  );
};
export default ReportsMainTable;
